import { ReactElement } from 'react'
import { Helmet } from 'react-helmet'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core'
import {
  createStyles,
  makeStyles,
  Theme
} from '@material-ui/core/styles'
import {
  Security,
  Speed,
  Gavel,
  ExpandMore,
  CheckCircle,
  AccountBalance,
  Lock
} from '@material-ui/icons'
import Urls from 'ustaxes/data/urls'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: '#FFFFFF',
      minHeight: '100vh'
    },
    // Hero
    hero: {
      backgroundColor: '#FFFFFF',
      padding: theme.spacing(10, 2, 8),
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 2, 4)
      }
    },
    heroInner: {
      maxWidth: 720,
      margin: '0 auto'
    },
    flag: {
      fontSize: 56,
      display: 'block',
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        fontSize: 40
      }
    },
    heroTitle: {
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.1,
      color: '#1A237E',
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem'
      }
    },
    heroHighlight: {
      color: '#1A237E'
    },
    heroSub: {
      fontSize: '1.2rem',
      color: '#3949AB',
      lineHeight: 1.7,
      maxWidth: 560,
      margin: '0 auto',
      marginBottom: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem'
      }
    },
    priceBox: {
      display: 'inline-flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(4),
      padding: theme.spacing(1.5, 4),
      borderRadius: 100,
      backgroundColor: '#F3F4F6',
      border: '1px solid #E5E7EB'
    },
    priceAmount: {
      fontWeight: 800,
      fontSize: '2rem',
      color: '#1A237E'
    },
    priceText: {
      fontSize: '0.95rem',
      color: '#3949AB'
    },
    ctaRow: {
      display: 'flex',
      gap: theme.spacing(2),
      justifyContent: 'center',
      flexWrap: 'wrap' as const,
      marginBottom: theme.spacing(3)
    },
    ctaPrimary: {
      padding: '14px 48px',
      fontSize: '1.05rem',
      fontWeight: 700,
      borderRadius: 8,
      textTransform: 'none',
      boxShadow: '0 4px 14px rgba(26,35,126,0.25)',
      '&:hover': {
        boxShadow: '0 6px 20px rgba(26,35,126,0.35)'
      }
    },
    ctaSecondary: {
      padding: '14px 48px',
      fontSize: '1.05rem',
      fontWeight: 600,
      borderRadius: 8,
      textTransform: 'none',
      borderWidth: 2,
      borderColor: '#1A237E',
      color: '#1A237E',
      '&:hover': {
        borderWidth: 2,
        backgroundColor: 'rgba(26,35,126,0.04)'
      }
    },
    trustRow: {
      display: 'flex',
      gap: theme.spacing(4),
      justifyContent: 'center',
      flexWrap: 'wrap' as const
    },
    trustItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: '0.85rem',
      color: '#3949AB'
    },
    trustIcon: {
      fontSize: 16,
      color: '#5C6BC0'
    },

    // Stats bar
    statsBar: {
      backgroundColor: '#1A237E',
      padding: theme.spacing(4, 2),
      color: '#FFFFFF'
    },
    statsGrid: {
      maxWidth: 900,
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-around',
      flexWrap: 'wrap' as const,
      gap: theme.spacing(3)
    },
    statItem: {
      textAlign: 'center'
    },
    statNumber: {
      fontWeight: 800,
      fontSize: '1.8rem'
    },
    statLabel: {
      fontSize: '0.85rem',
      opacity: 0.8,
      marginTop: 2
    },

    // Features
    featuresSection: {
      padding: theme.spacing(10, 2),
      backgroundColor: '#FAFAFA',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 2)
      }
    },
    sectionTitle: {
      textAlign: 'center',
      fontWeight: 800,
      fontSize: '2rem',
      color: '#1A237E',
      marginBottom: theme.spacing(1)
    },
    sectionSub: {
      textAlign: 'center',
      color: '#3949AB',
      fontSize: '1.05rem',
      marginBottom: theme.spacing(6),
      maxWidth: 500,
      margin: '0 auto'
    },
    featureCard: {
      height: '100%',
      border: '1px solid #E5E7EB',
      boxShadow: 'none',
      borderRadius: 12,
      backgroundColor: '#FFFFFF',
      transition: 'box-shadow 0.2s ease',
      '&:hover': {
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }
    },
    featureCardContent: {
      padding: theme.spacing(4, 3)
    },
    featureIconBox: {
      width: 48,
      height: 48,
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(2)
    },
    featureTitle: {
      fontWeight: 700,
      fontSize: '1.1rem',
      color: '#1A237E',
      marginBottom: theme.spacing(1)
    },
    featureDesc: {
      color: '#3949AB',
      fontSize: '0.92rem',
      lineHeight: 1.6
    },

    // How it works
    howSection: {
      padding: theme.spacing(10, 2),
      backgroundColor: '#FFFFFF',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 2)
      }
    },
    stepCard: {
      textAlign: 'center',
      padding: theme.spacing(3)
    },
    stepNumber: {
      width: 40,
      height: 40,
      borderRadius: '50%',
      backgroundColor: '#1A237E',
      color: '#FFFFFF',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 800,
      fontSize: '1.1rem',
      marginBottom: theme.spacing(2)
    },
    stepTitle: {
      fontWeight: 700,
      fontSize: '1.05rem',
      color: '#1A237E',
      marginBottom: theme.spacing(1)
    },
    stepDesc: {
      color: '#3949AB',
      fontSize: '0.9rem',
      lineHeight: 1.6
    },

    // Forms section
    formsSection: {
      padding: theme.spacing(8, 2),
      backgroundColor: '#FAFAFA',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 2)
      }
    },
    accordion: {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: '8px !important',
      marginBottom: theme.spacing(1.5),
      boxShadow: 'none',
      '&::before': {
        display: 'none'
      }
    },
    formList: {
      columns: 2,
      columnGap: theme.spacing(4),
      listStyle: 'none',
      padding: 0,
      margin: 0,
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        columns: 1
      }
    },
    formListItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 0),
      fontSize: '0.9rem',
      color: '#283593'
    },
    checkIcon: {
      fontSize: 16,
      color: '#059669',
      flexShrink: 0
    },

    // CTA bottom
    ctaBottom: {
      padding: theme.spacing(10, 2),
      textAlign: 'center',
      backgroundColor: '#FFFFFF',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(6, 2)
      }
    },

    // Footer
    footer: {
      textAlign: 'center',
      padding: theme.spacing(4, 2),
      borderTop: '1px solid #E5E7EB',
      color: '#5C6BC0',
      fontSize: '0.85rem',
      backgroundColor: '#FFFFFF'
    }
  })
)

export default function GettingStarted(): ReactElement {
  const classes = useStyles()

  const features = [
    {
      icon: <Speed style={{ color: '#1A237E', fontSize: 24 }} />,
      bg: '#EEF2FF',
      title: 'AI-Assisted Filing',
      desc: 'Smart tax assistant answers questions, catches errors, and guides you through every step of your return.'
    },
    {
      icon: <Lock style={{ color: '#059669', fontSize: 24 }} />,
      bg: '#ECFDF5',
      title: 'Bank-Level Security',
      desc: 'Your data is encrypted end-to-end. Self-hosted infrastructure keeps your information private and protected.'
    },
    {
      icon: <AccountBalance style={{ color: '#B71C1C', fontSize: 24 }} />,
      bg: '#FEF2F2',
      title: 'Federal & State',
      desc: 'File both federal 1040 and supported state returns. W-2s, 1099s, deductions, and credits all included.'
    },
    {
      icon: <Gavel style={{ color: '#92400E', fontSize: 24 }} />,
      bg: '#FFFBEB',
      title: 'IRS Compliant',
      desc: 'Generated returns meet all IRS specifications. Print, mail, or use with any e-file provider.'
    }
  ]

  const supportedForms = [
    'W-2 (Wages & Salary)',
    '1099-INT (Interest Income)',
    '1099-DIV (Dividend Income)',
    '1099-B (Brokerage Statements)',
    '1098-E (Student Loan Interest)',
    '1099-R (Retirement Distributions)',
    'SSA-1099 (Social Security)',
    'Schedule A (Itemized Deductions)',
    'Schedule B (Interest & Dividends)',
    'Schedule D (Capital Gains)',
    'Schedule E (Rental Income)',
    'Schedule SE (Self-Employment Tax)',
    'Form 8812 (Child Tax Credit)',
    'Form 8889 (HSA Contributions)',
    'Form 8949 (Investment Sales)',
    'Form 8959 (Additional Medicare)',
    'Form 8960 (Net Investment Income)',
    'Form 8995 (QBI Deduction)',
    'Form 6251 (Alternative Minimum Tax)'
  ]

  const supportedCredits = [
    'Child Tax Credit (CTC)',
    'Earned Income Tax Credit (EITC)',
    'Additional Child Tax Credit (ACTC)'
  ]

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Ameritax - AI-Powered Tax Filing for Americans | Federal &amp; State Returns</title>
        <meta
          name="description"
          content="File your federal and state tax returns with confidence. AI-assisted tax filing for $20 per return. W-2s, 1099s, deductions, credits - all supported. IRS compliant."
        />
        <meta name="keywords" content="tax filing, tax return, federal taxes, state taxes, 1040, W-2, 1099, tax deductions, tax credits, AI tax assistant, ameritax" />
        <meta property="og:title" content="Ameritax - AI-Powered Tax Filing for Americans" />
        <meta property="og:description" content="File your federal and state tax returns with AI-assisted guidance. $20 per return. Secure, accurate, IRS compliant." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ameritax.xyz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ameritax - AI-Powered Tax Filing" />
        <meta name="twitter:description" content="File your taxes the smart way. AI-assisted, $20/return, federal and state included." />
        <link rel="canonical" href="https://ameritax.xyz" />
      </Helmet>

      {/* === HERO === */}
      <Box className={classes.hero}>
        <Box className={classes.heroInner}>
          <span className={classes.flag} role="img" aria-label="American flag">
            {'\u{1F1FA}\u{1F1F8}'}
          </span>

          <Typography variant="h1" className={classes.heroTitle}>
            Tax filing,{' '}
            <span className={classes.heroHighlight}>simplified.</span>
          </Typography>

          <Typography className={classes.heroSub}>
            AI-powered tax preparation for Americans who want accuracy
            without complexity. Federal and state returns, done right.
          </Typography>

          <Box className={classes.priceBox}>
            <Typography className={classes.priceAmount}>$20</Typography>
            <Typography className={classes.priceText}>
              per return &middot; Federal + State
            </Typography>
          </Box>

          <Box className={classes.ctaRow}>
            <Button
              variant="contained"
              color="primary"
              className={classes.ctaPrimary}
              component={RouterLink}
              to={Urls.taxPayer.info}
            >
              Start My Return
            </Button>
            <Button
              variant="outlined"
              className={classes.ctaSecondary}
              component={RouterLink}
              to={Urls.login}
            >
              Sign In
            </Button>
          </Box>

          <Box className={classes.trustRow}>
            <span className={classes.trustItem}>
              <Lock className={classes.trustIcon} /> 256-bit encryption
            </span>
            <span className={classes.trustItem}>
              <CheckCircle className={classes.trustIcon} /> IRS compliant
            </span>
            <span className={classes.trustItem}>
              <Security className={classes.trustIcon} /> Self-hosted
            </span>
          </Box>
        </Box>
      </Box>

      {/* === STATS BAR === */}
      <Box className={classes.statsBar}>
        <Box className={classes.statsGrid}>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber}>19+</Typography>
            <Typography className={classes.statLabel}>Forms Supported</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber}>50</Typography>
            <Typography className={classes.statLabel}>States Covered</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber}>AI</Typography>
            <Typography className={classes.statLabel}>Powered Assistant</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statNumber}>$20</Typography>
            <Typography className={classes.statLabel}>Flat Rate</Typography>
          </Box>
        </Box>
      </Box>

      {/* === FEATURES === */}
      <Box className={classes.featuresSection}>
        <Container maxWidth="md">
          <Typography variant="h2" className={classes.sectionTitle}>
            Everything you need to file
          </Typography>
          <Typography className={classes.sectionSub}>
            Professional-grade tax preparation with AI guidance at every step.
          </Typography>

          <Grid container spacing={3} style={{ marginTop: 16 }}>
            {features.map((f, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card className={classes.featureCard} elevation={0}>
                  <CardContent className={classes.featureCardContent}>
                    <Box
                      className={classes.featureIconBox}
                      style={{ backgroundColor: f.bg }}
                    >
                      {f.icon}
                    </Box>
                    <Typography className={classes.featureTitle}>
                      {f.title}
                    </Typography>
                    <Typography className={classes.featureDesc}>
                      {f.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* === HOW IT WORKS === */}
      <Box className={classes.howSection}>
        <Container maxWidth="md">
          <Typography variant="h2" className={classes.sectionTitle}>
            How it works
          </Typography>
          <Typography className={classes.sectionSub}>
            Three simple steps to your completed tax return.
          </Typography>

          <Grid container spacing={4} style={{ marginTop: 16 }}>
            {[
              {
                num: '1',
                title: 'Enter your information',
                desc: 'Import W-2s, 1099s, and other income documents. Our AI verifies accuracy as you go.'
              },
              {
                num: '2',
                title: 'Review deductions & credits',
                desc: 'We identify eligible deductions and credits to maximize your refund. Nothing missed.'
              },
              {
                num: '3',
                title: 'File with confidence',
                desc: 'Review your completed return, generate IRS-compliant forms, and file. Federal and state included.'
              }
            ].map((step) => (
              <Grid item xs={12} sm={4} key={step.num}>
                <Box className={classes.stepCard}>
                  <Box className={classes.stepNumber}>{step.num}</Box>
                  <Typography className={classes.stepTitle}>
                    {step.title}
                  </Typography>
                  <Typography className={classes.stepDesc}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* === SUPPORTED FORMS === */}
      <Box className={classes.formsSection}>
        <Container maxWidth="sm">
          <Typography variant="h2" className={classes.sectionTitle}>
            Comprehensive coverage
          </Typography>
          <Typography className={classes.sectionSub}>
            All the forms and schedules you need, in one place.
          </Typography>

          <Box mt={4}>
            <Accordion className={classes.accordion} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography style={{ fontWeight: 600, color: '#1A237E' }}>
                  Income Forms &amp; Schedules ({supportedForms.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className={classes.formList}>
                  {supportedForms.map((form) => (
                    <li key={form} className={classes.formListItem}>
                      <CheckCircle className={classes.checkIcon} />
                      {form}
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography style={{ fontWeight: 600, color: '#1A237E' }}>
                  Tax Credits ({supportedCredits.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className={classes.formList}>
                  {supportedCredits.map((credit) => (
                    <li key={credit} className={classes.formListItem}>
                      <CheckCircle className={classes.checkIcon} />
                      {credit}
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion className={classes.accordion} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography style={{ fontWeight: 600, color: '#1A237E' }}>
                  States Without Income Tax
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ul className={classes.formList}>
                  {[
                    'Alaska', 'Florida', 'Nevada', 'New Hampshire',
                    'South Dakota', 'Tennessee', 'Texas', 'Washington', 'Wyoming'
                  ].map((state) => (
                    <li key={state} className={classes.formListItem}>
                      <CheckCircle className={classes.checkIcon} />
                      {state}
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Container>
      </Box>

      {/* === BOTTOM CTA === */}
      <Box className={classes.ctaBottom}>
        <Typography variant="h3" style={{ fontWeight: 800, color: '#1A237E', marginBottom: 12 }}>
          Ready to file?
        </Typography>
        <Typography style={{ color: '#3949AB', fontSize: '1.05rem', maxWidth: 400, margin: '0 auto 32px' }}>
          Join Americans who file smarter. Start free, pay only when you&apos;re ready to file.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.ctaPrimary}
          component={RouterLink}
          to={Urls.taxPayer.info}
          size="large"
        >
          Start Filing Now
        </Button>
      </Box>

      {/* === FOOTER === */}
      <Box className={classes.footer}>
        <Typography variant="body2" style={{ marginBottom: 4 }}>
          <strong>Ameritax.xyz</strong> &mdash; AI-Assisted Tax Filing
        </Typography>
        <Typography variant="caption">
          Built in the USA. Hosted on Cloudflare.
        </Typography>
      </Box>
    </div>
  )
}
